import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alerts from './Alerts';
import GuestCharging from './GuestCharging';
import LiveLogsSession from './LiveLogsSession';
import StationActivity from './StationActivity';
import StationDetails from './StationDetails';
import Transactions from './Transactions';
import UpcomingMaintenanceSchedule from './UpcomingMaintenanceSchedule';
import Uptime from './Uptime';

interface StationDetailsTabProperties {
  label: string;
  key: string;
  isActive: boolean;
}

/**
 * Station management bottom container component
 * @returns {any} The rendered component
 */
function StationManagementBottomContainer() {
  const { t, i18n } = useTranslation('sidePanel');
  const [activeTabKey, setActiveTabKey] = useState<string>('stationDetails');

  const stationDetailsTab = useMemo<StationDetailsTabProperties[]>(
    () => [
      {
        label: t('stationDetails'),
        key: 'stationDetails',
        isActive: activeTabKey === 'stationDetails',
      },
      {
        label: t('stationActivity'),
        key: 'stationActivity',
        isActive: activeTabKey === 'stationActivity',
      },
      {
        label: t('liveLogsSession'),
        key: 'liveLogsSession',
        isActive: activeTabKey === 'liveLogsSession',
      },
      {
        label: t('alerts'),
        key: 'alerts',
        isActive: activeTabKey === 'alerts',
      },
      {
        label: t('transactions'),
        key: 'transaction',
        isActive: activeTabKey === 'transaction',
      },
      {
        label: t('guestCharging'),
        key: 'guestCharging',
        isActive: activeTabKey === 'guestCharging',
      },
      {
        label: t('upcomingMaintenanceSchedule'),
        key: 'upcomingMaintenanceSchedule',
        isActive: activeTabKey === 'upcomingMaintenanceSchedule',
      },
      {
        label: t('upTime'),
        key: 'upTime',
        isActive: activeTabKey === 'upTime',
      },
    ],
    [t, activeTabKey],
  );

  /**
   * Function to switch the tab
   * @param {string} key The key of the tab to switch to
   */
  const handleSwitchTab = (key: string) => {
    setActiveTabKey(key);
  };

  return (
    <div className="station-management-bottom-wrap" dir={i18n.dir()}>
      <div className="station-management-bottom-tab">
        {stationDetailsTab.map((tab) => (
          <div
            key={tab.key}
            className={`each-tab ${tab.isActive ? 'active' : ''}`}
            role="presentation"
            onClick={() => handleSwitchTab(tab.key)}>
            <h6>{tab.label}</h6>
            <div className="bottom-green" />
          </div>
        ))}
      </div>
      <div className="station-management-bottom-container">
        {(() => {
          switch (activeTabKey) {
            case 'stationDetails':
              return <StationDetails />;
            case 'stationActivity':
              return <StationActivity />;
            case 'liveLogsSession':
              return <LiveLogsSession />;
            case 'alerts':
              return <Alerts />;
            case 'transaction':
              return <Transactions />;
            case 'guestCharging':
              return <GuestCharging />;
            case 'upcomingMaintenanceSchedule':
              return <UpcomingMaintenanceSchedule />;
            case 'upTime':
              return <Uptime />;
            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
}

export default StationManagementBottomContainer;
